import { cloneElement, ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { IconProps } from '@components/icons/base';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export interface BaseTab {
  label: string;
  content: ReactNode;
  counter?: number;
  icon?: ReactElement<IconProps>;
  dataTestId?: string;
}

export type TabActiveProps = {
  $active: boolean;
};

export const Label = styled(Text).attrs({ font: 'semibold14' })<TabActiveProps>(({ $active }) => ({
  color: $active ? theme.color.textPrimary : theme.color.textHelper,
  transition: transitionStandard('color'),
}));

export const TabActiveBorder = styled.div<TabActiveProps>(({ $active }) => ({
  backgroundColor: $active ? theme.color.elementPurpleInvert : 'transparent',
  width: '100%',
  height: pxToRem(4),
  transition: transitionStandard('all'),
  transform: $active ? 'scaleX(1) translateX(0px)' : 'scaleX(0) translateX(50%)',
  borderTopLeftRadius: pxToRem(2),
  borderTopRightRadius: pxToRem(2),
}));

export type TabContentProps = {
  tab: BaseTab;
  isActive: boolean;
};

export const TabContent = ({ tab, isActive }: TabContentProps) => {
  return (
    <Stack space={4}>
      <Stack space={1} orientation="horizontal" align="center" justify="center">
        {tab.icon &&
          cloneElement(tab.icon, {
            ...tab.icon.props,
            size: 16,
            color: isActive ? 'textPrimary' : 'textHelper',
          })}
        <Label $active={isActive} text={tab.label} />
        {tab.counter !== undefined && <Badge text={tab.counter} />}
      </Stack>
      <TabActiveBorder $active={isActive} />
    </Stack>
  );
};

export const baseTabStyles = ({ $active }: TabActiveProps) => ({
  marginRight: pxToRem(24),
  position: 'relative' as const,
  color: $active ? theme.color.textPrimary : theme.color.textHelper,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer' as const,
  outline: 'inherit',
  textDecoration: 'none',
  ...(!$active && {
    ':hover': {
      cursor: 'pointer',
      color: theme.color.textSecondary,
      [`${TabActiveBorder}`]: {
        backgroundColor: theme.color.uiTrans20,
        transform: 'scaleX(1) translateX(0px)',
      },
      [`${Label}`]: {
        color: theme.color.textSecondary,
      },
      svg: {
        color: theme.color.textSecondary,
      },
    },
  }),
});
